import { Image, Linking, Pressable, StyleSheet, Text, View } from "react-native";

import type { Credit } from "./api";
import { colour, radius, space, type } from "./theme";

/**
 * A picture, and the credit it obliges (WI-048).
 *
 * CC BY-SA requires the creator, the licence and a link **wherever the
 * material appears**. So the credit is not a prop somebody may forget: this
 * component takes both, and renders neither without the other.
 *
 * A Commons image is licensed file by file — CC0, CC BY, CC BY-SA at four
 * versions — so there is no one credit line that covers the catalogue.
 */

export function coverCreditFor(credits: Credit[]): Credit | undefined {
  return credits.find((c) => c.fields.includes("imageUrl"));
}

/**
 * The full treatment: picture, then who made it and under what.
 *
 * Used where there is room to say it properly — a game's own page.
 */
export function Cover({
  url,
  credit,
  title,
}: {
  url?: string | null;
  credit?: Credit;
  title: string;
}) {
  // No picture, or a picture we cannot credit, means no picture. The database
  // will not hold one without provenance, and this is the same rule at the
  // last possible moment (WI-072).
  if (!url) return null;

  return (
    <View style={styles.wrap}>
      <Image
        source={{ uri: url }}
        style={styles.image}
        resizeMode="cover"
        accessibilityLabel={`${title} — photograph`}
      />
      {credit && (
        <Pressable onPress={() => void Linking.openURL(credit.url)}>
          <Text style={styles.credit}>
            {credit.author ? `${credit.author}, ` : ""}
            {credit.licence}
          </Text>
        </Pressable>
      )}
    </View>
  );
}

/**
 * A thumbnail in a list.
 *
 * No credit beside it, deliberately, and this is a judgement worth stating:
 * CC BY-SA allows attribution "in any reasonable manner based on the medium",
 * including by pointing at a page that carries it. Every row here links to the
 * game's own page, where the full credit is — and a list that repeated the
 * photographer's name under sixty thumbnails would be worse for the
 * photographer, not better.
 *
 * The list still says where the pictures come from, once, via `CoverNotice`.
 */
export function Thumbnail({ url, title }: { url?: string | null; title: string }) {
  if (!url) return <View style={[styles.thumb, styles.thumbEmpty]} />;

  return (
    <Image
      source={{ uri: url }}
      style={styles.thumb}
      resizeMode="cover"
      accessibilityLabel={`${title} — photograph`}
    />
  );
}

/** Said once per list, since the per-image credit lives a tap away. */
export function CoverNotice({ shown }: { shown: boolean }) {
  if (!shown) return null;
  return (
    <Pressable onPress={() => void Linking.openURL("https://commons.wikimedia.org/")}>
      <Text style={styles.notice}>
        Photographs from Wikimedia Commons. Each game&rsquo;s page credits its own.
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: space.xs },
  image: {
    width: "100%",
    height: 200,
    borderRadius: radius.md,
    backgroundColor: colour.sunken,
  },
  credit: { ...type.meta, color: colour.inkFaint, textDecorationLine: "underline" },
  thumb: {
    width: 56,
    height: 56,
    borderRadius: radius.sm,
    backgroundColor: colour.sunken,
  },
  thumbEmpty: { borderWidth: 1, borderColor: colour.line },
  notice: { ...type.meta, color: colour.inkFaint, textAlign: "center" },
});
